// Pure helpers for the client workout player (pages/WorkoutPlayerPage.js).
//
// Flow per set: "ready" (exercise on screen, client taps START when ready)
// -> "work" (set in progress, client taps when done) -> "rest" (countdown,
// client can log reps/weight of the set just finished) -> next set's "ready".
// After the last set of the last exercise the player goes to "summary".
//
// Warm-up sets (plan `warmup_sets`) come first within their exercise: `sets` of a
// queue item counts them too, `working` only the working sets; setAt() says which is which.
//
// Superset: exercises sharing `superset_group` next to each other alternate set
// by set with no rest between them; the rest comes after the last one of a round.
//
// All timers are derived from one pausable session clock (ms of active
// training time) instead of decrementing intervals, so pausing freezes
// every timer at once and a backgrounded tab doesn't drift.

import { WARMUP_REST_SECONDS, warmupsOf } from './warmupSets';

export const DEFAULT_REST_SECONDS = 60;

export const buildQueue = (plan, day, exercisesById) =>
  plan.exercises
    .filter(ex => ex.day_number === day)
    .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
    .map(planEx => {
      const lib = exercisesById[planEx.exercise_id] || {};
      const warmups = warmupsOf(planEx);
      const working = Math.max(1, planEx.sets || 1);
      return {
        exercise_id: planEx.exercise_id,
        name: planEx.exercise_name,
        warmups,
        working,
        sets: warmups.length + working,
        reps: planEx.reps || '',
        weight: planEx.weight_kg ?? null,
        tempo: planEx.tempo || lib.tempo || null,
        rir: planEx.rir,
        rpe: planEx.rpe,
        rest: planEx.rest_seconds ?? lib.rest_seconds ?? DEFAULT_REST_SECONDS,
        superset: planEx.superset_group || null,
        block: planEx.circuit_group || null,
        notes: planEx.notes || null,
        cues: lib.cues || [],
        steps: lib.execution_steps || [],
        animation_url: lib.animation_url || null,
        animation_frames: lib.animation_frames || [],
        video_url: lib.video_url || null,
        image_url: lib.image_url || null,
        library: lib.id ? lib : null,
      };
    });

// What set `setIdx` of a queue item is: a warm-up set (numbered among warm-ups) or a working one.
export const setAt = (ex, setIdx) => {
  const warm = ex.warmups?.length || 0;
  if (setIdx < warm) {
    const w = ex.warmups[setIdx];
    return { warmup: true, n: setIdx + 1, of: warm, reps: w.reps, weight: w.weight, percent: w.percent,
      label: `Rozgrzewkowa ${setIdx + 1}/${warm}`, short: `Rozgrz. ${setIdx + 1}` };
  }
  const n = setIdx - warm + 1;
  const of = ex.working ?? ex.sets;
  return { warmup: false, n, of, reps: ex.reps, weight: ex.weight, label: `Seria ${n}/${of}`, short: `Seria ${n}` };
};

// Queue indexes of the superset holding `exIdx` (just `exIdx` outside a superset).
export const supersetOf = (queue, exIdx) => {
  const group = queue[exIdx].superset;
  if (!group) return [exIdx];
  let [start, end] = [exIdx, exIdx];
  while (start > 0 && queue[start - 1].superset === group) start -= 1;
  while (end + 1 < queue.length && queue[end + 1].superset === group) end += 1;
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

export const nextPosition = (queue, exIdx, setIdx) => {
  const group = supersetOf(queue, exIdx);
  const sameRound = group.find(i => i > exIdx && setIdx < queue[i].sets);
  if (sameRound !== undefined) return { exIdx: sameRound, setIdx };
  const nextRound = group.find(i => setIdx + 1 < queue[i].sets);
  if (nextRound !== undefined) return { exIdx: nextRound, setIdx: setIdx + 1 };
  const after = group[group.length - 1] + 1;
  return after < queue.length ? { exIdx: after, setIdx: 0 } : null;
};

// Seconds of rest after this set: none inside a superset round, then the last exercise's rest.
export const restAfter = (queue, exIdx, setIdx) => {
  if (setAt(queue[exIdx], setIdx).warmup) return Math.min(WARMUP_REST_SECONDS, queue[exIdx].rest);
  const group = supersetOf(queue, exIdx);
  const next = nextPosition(queue, exIdx, setIdx);
  if (next && next.exIdx > exIdx && group.includes(next.exIdx)) return 0;
  return queue[group[group.length - 1]].rest;
};

// The position right after the whole exercise (or whole superset).
export const skipTarget = (queue, exIdx) => {
  const group = supersetOf(queue, exIdx);
  return { exIdx: group[group.length - 1], setIdx: Math.max(...group.map(i => queue[i].sets)) - 1 };
};

// Session clock: { accumulated, resumedAt } - resumedAt is null while paused.
export const clockNow = (clock, now = Date.now()) =>
  clock.accumulated + (clock.resumedAt ? now - clock.resumedAt : 0);

export const pauseClock = (clock, now = Date.now()) =>
  clock.resumedAt ? { accumulated: clockNow(clock, now), resumedAt: null } : clock;

export const resumeClock = (clock, now = Date.now()) =>
  clock.resumedAt ? clock : { ...clock, resumedAt: now };

export const formatTime = (ms) => {
  const total = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(h ? 2 : 1, '0');
  const ss = String(s).padStart(2, '0');
  return h ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
};

// Reps in the plan can be a range ("8-12"); prefill the log with the top of it.
export const defaultRepsFor = (reps) => {
  const nums = String(reps || '').match(/\d+/g);
  return nums ? nums[nums.length - 1] : '';
};

// Collapse per-set results into the WorkoutExerciseLog shape; actual_* describe working sets only.
export const toWorkoutLogExercises = (queue, results) =>
  queue.map((ex, i) => {
    const done = (results[i] || []).map((st, idx) => st && { ...st, warmup: setAt(ex, idx).warmup }).filter(Boolean);
    const sets = done.filter(st => !st.warmup);
    const weights = sets.map(s => parseFloat(s.weight)).filter(w => !Number.isNaN(w));
    return {
      exercise_id: ex.exercise_id,
      exercise_name: ex.name,
      completed: done.length > 0,
      actual_sets: sets.length || null,
      actual_reps: sets.length ? sets.map(s => s.reps || '?').join(', ') : null,
      actual_weight: weights.length ? Math.max(...weights) : null,
      notes: null,
      // Per-set results (warm-ups marked); the backend derives the summary fields above from these too.
      sets: done.map(st => {
        const w = parseFloat(String(st.weight ?? '').replace(',', '.'));
        return { reps: String(st.reps ?? '').trim() || null, weight: Number.isFinite(w) ? w : null, done: true, warmup: st.warmup };
      }),
    };
  });

export const storageKey = (planId, day) => `hs-workout-player:${planId}:${day}`;

export const loadSaved = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const saveState = (key, state) => {
  try {
    localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // storage unavailable (private mode) - player still works, just not resumable
  }
};

export const clearSaved = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
};

// Short beep + vibration when rest ends; silently no-ops where unsupported.
export const signalRestEnd = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (Ctx) {
      const ctx = new Ctx();
      [0, 0.25, 0.5].forEach((t, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = i === 2 ? 1320 : 880;
        gain.gain.setValueAtTime(0.25, ctx.currentTime + t);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + 0.18);
        osc.connect(gain).connect(ctx.destination);
        osc.start(ctx.currentTime + t);
        osc.stop(ctx.currentTime + t + 0.2);
      });
      setTimeout(() => ctx.close(), 1000);
    }
  } catch {
    // ignore
  }
  try {
    navigator.vibrate?.([200, 100, 200]);
  } catch {
    // ignore
  }
};
